import React, { useState, useEffect } from 'react';
import {
  Truck, Package, MapPin, Star, ChevronRight, CheckCircle,
  Clock, Shield, TrendingUp, Users, ArrowRight, Menu, X,
  Zap, DollarSign, BarChart2, AlertCircle, RefreshCw, Lock, Leaf, Globe
} from 'lucide-react';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

export default function LandingPage({ onIniciarSesion, onRegistrarCliente, onRegistrarTransportista, onRegistrar }) {
  const [tab, setTab] = useState('cliente');
  const [navAbierto, setNavAbierto] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [redCfg, setRedCfg] = useState(null);
  const [redCfgCargando, setRedCfgCargando] = useState(true);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    fetch(`${BASE_URL}/admin/config`)
      .then(r => r.json())
      .then(d => { if (d?.config?.redMercadeo) setRedCfg(d.config.redMercadeo); })
      .catch(() => {})
      .finally(() => setRedCfgCargando(false));
  }, []);

  const stats = [
    { valor: '<15 min', label: 'Para recibir tu primera cotización' },
    { valor: '100%',    label: 'Transportistas verificados SCT' },
    { valor: 'Escrow',  label: 'Tu dinero protegido hasta la entrega' },
    { valor: '−50%',    label: 'Menos CO₂ por tonelada en fletes de retorno' },
  ];

  const pasosCliente = [
    { icono: <Package size={28} />, titulo: 'Publica tu solicitud', desc: 'Describe tu carga, origen, destino y fecha. Menos de 2 minutos. Sin llamadas.' },
    { icono: <Truck size={28} />,   titulo: 'Elige la mejor oferta', desc: 'Transportistas verificados te envían precios. Tú comparas y decides sin presión.' },
    { icono: <Lock size={28} />,    titulo: 'Tu dinero protegido', desc: 'Pagas al aceptar, pero el dinero llega al transportista solo cuando confirmas que tu carga llegó bien.' },
  ];

  const pasosTransportista = [
    { icono: <Users size={28} />,      titulo: 'Registra tu empresa', desc: 'Sube tus documentos SCT y permisos. Aprobación en 24 horas. Sin burocracia.' },
    { icono: <Zap size={28} />,        titulo: 'Recibe cargas por tu ruta', desc: 'Ve solicitudes que coinciden con tus viajes. Cotiza en segundos desde el panel.' },
    { icono: <RefreshCw size={28} />,  titulo: 'Llena el viaje de regreso', desc: 'Publica tus retornos vacíos y encuentra clientes que paguen ese espacio. Dinero extra sin esfuerzo.' },
  ];

  const beneficiosCliente = [
    { icono: <Lock size={20} />,       texto: 'Pago en garantía — liberado solo cuando confirmas la entrega' },
    { icono: <Shield size={20} />,     texto: 'Transportistas verificados con permisos SCT y seguro de carga' },
    { icono: <Clock size={20} />,      texto: 'Cotizaciones en menos de 15 minutos, no en días de llamadas' },
    { icono: <MapPin size={20} />,     texto: 'Rastreo GPS en tiempo real desde recolección hasta entrega' },
    { icono: <CheckCircle size={20}/>, texto: 'Carta porte digital generada automáticamente en cada viaje' },
    { icono: <Star size={20} />,       texto: 'Calificaciones reales — siempre sabes con quién trabajas' },
  ];

  const beneficiosTransportista = [
    { icono: <DollarSign size={20} />, texto: 'Llenar retornos vacíos — convierte viajes de regreso en ingresos' },
    { icono: <TrendingUp size={20} />, texto: 'Clientes nuevos sin vendedores ni publicidad' },
    { icono: <BarChart2 size={20} />,  texto: 'Panel completo: choferes, unidades, cobros y facturas' },
    { icono: <Zap size={20} />,        texto: 'Pago garantizado — el escrow protege a ambas partes' },
    { icono: <CheckCircle size={20}/>, texto: 'CFDI automático por cada servicio — sin trámites extra' },
    { icono: <Shield size={20} />,     texto: 'Reputación verificada que atrae mejores clientes' },
  ];

  return (
    <div className="min-h-screen bg-gray-950 font-sans">

      {/* ── NAV ── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-gray-950 shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <Truck size={20} className="text-white" />
            </div>
            <div>
              <p className="text-orange-400 font-extrabold text-lg leading-none">Choyero!</p>
              <p className="text-gray-400 text-[9px] font-semibold uppercase tracking-widest leading-none">Marketplace Logístico</p>
            </div>
          </div>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-6">
            <a href="#como-funciona" className="text-gray-300 hover:text-white text-sm transition">Cómo funciona</a>
            <a href="#beneficios" className="text-gray-300 hover:text-white text-sm transition">Beneficios</a>
            <a href="#referidos" className="text-gray-300 hover:text-white text-sm transition">Programa de referidos</a>
            <a href="/quienessomos" className="text-gray-300 hover:text-white text-sm transition">Quiénes somos</a>
            <button
              onClick={onIniciarSesion}
              className="text-gray-300 hover:text-white text-sm transition"
            >
              Iniciar sesión
            </button>
            <button
              onClick={onRegistrar}
              className="border border-gray-600 hover:border-orange-500 text-white font-semibold px-4 py-2 rounded-lg text-sm transition"
            >
              Registrarme
            </button>
          </div>

          {/* Mobile hamburger */}
          <button className="md:hidden text-white" onClick={() => setNavAbierto(!navAbierto)}>
            {navAbierto ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        {navAbierto && (
          <div className="md:hidden bg-gray-950 border-t border-gray-800 px-6 py-4 space-y-3">
            <a href="#como-funciona" onClick={() => setNavAbierto(false)} className="block text-gray-300 hover:text-white text-sm">Cómo funciona</a>
            <a href="#beneficios" onClick={() => setNavAbierto(false)} className="block text-gray-300 hover:text-white text-sm">Beneficios</a>
            <a href="#referidos" onClick={() => setNavAbierto(false)} className="block text-gray-300 hover:text-white text-sm">Programa de referidos</a>
            <a href="/quienessomos" onClick={() => setNavAbierto(false)} className="block text-gray-300 hover:text-white text-sm">Quiénes somos</a>
            <button onClick={onIniciarSesion} className="w-full border border-gray-700 text-white py-2.5 rounded-lg text-sm">
              Iniciar sesión
            </button>
            <button onClick={onRegistrar} className="w-full bg-orange-500 text-white font-semibold py-2.5 rounded-lg text-sm">
              Registrarme
            </button>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section className="relative min-h-screen flex items-center bg-gray-950 overflow-hidden">
        {/* Fondo decorativo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-orange-500 rounded-full opacity-10 blur-3xl" />
          <div className="absolute bottom-0 -left-20 w-80 h-80 bg-orange-600 rounded-full opacity-5 blur-3xl" />
          {/* Grid lines */}
          <div className="absolute inset-0 opacity-5"
            style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }}
          />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 pt-24 pb-16 flex flex-col lg:flex-row items-center gap-16">
          {/* Copy */}
          <div className="flex-1 text-center lg:text-left">
            <div className="flex flex-wrap gap-2 justify-center lg:justify-start mb-6">
              <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-4 py-1.5">
                <span className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
                <span className="text-orange-400 text-sm font-medium">Baja California y Baja California Sur</span>
              </div>
              <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-full px-4 py-1.5">
                <Leaf size={13} className="text-green-400" />
                <span className="text-green-400 text-sm font-medium">Logística con menor huella de carbono</span>
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Deja de llamar.<br />
              <span className="text-orange-400">Empieza a cotizar.</span>
            </h1>
            <p className="text-gray-300 text-lg md:text-xl mb-4 max-w-xl mx-auto lg:mx-0">
              Publica tu carga y recibe cotizaciones de transportistas verificados en menos de 15 minutos.
              Tu dinero está protegido hasta que confirmas que llegó todo bien.
            </p>
            <div className="flex items-center gap-3 justify-center lg:justify-start mb-10">
              <div className="flex items-center gap-1.5 bg-green-500/10 border border-green-500/30 rounded-full px-3 py-1">
                <Lock size={12} className="text-green-400" />
                <span className="text-green-400 text-xs font-semibold">Pago en garantía · Se libera al confirmar entrega</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <button
                onClick={onRegistrarCliente}
                className="group flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-8 py-4 rounded-xl text-lg transition shadow-lg shadow-orange-500/25"
              >
                <Package size={20} />
                Cotiza tu flete
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={onRegistrarTransportista}
                className="flex items-center justify-center gap-2 border border-gray-600 hover:border-orange-500 text-white font-semibold px-8 py-4 rounded-xl text-lg transition"
              >
                <Truck size={20} className="text-orange-400" />
                Soy transportista
              </button>
            </div>
            <p className="text-gray-400 text-sm mt-4 text-center lg:text-left">
              ¿Ya tienes cuenta?{' '}
              <button onClick={onIniciarSesion} className="text-orange-400 hover:text-orange-300 font-medium transition">
                Inicia sesión aquí
              </button>
            </p>
          </div>

          {/* Tarjeta flotante — antes/después */}
          <div className="flex-1 max-w-sm w-full space-y-3">
            {/* Antes */}
            <div className="bg-red-950/40 border border-red-800/40 rounded-2xl p-5">
              <p className="text-red-400 text-xs font-bold uppercase tracking-widest mb-3 flex items-center gap-1.5">
                <AlertCircle size={12} /> Sin Choyero! Marketplace Logístico
              </p>
              <div className="space-y-2 text-sm text-gray-400">
                <p>❌ Llamas a 5 transportistas y 3 no contestan</p>
                <p>❌ Cotizaciones que llegan al día siguiente</p>
                <p>❌ Pagas y rezas para que llegue tu carga</p>
                <p>❌ No sabes dónde está el camión</p>
                <p>❌ Sin comprobante fiscal automático</p>
              </div>
            </div>
            {/* Después */}
            <div className="bg-green-950/40 border border-green-700/40 rounded-2xl p-5">
              <p className="text-green-400 text-xs font-bold uppercase tracking-widest mb-3 flex items-center gap-1.5">
                <CheckCircle size={12} /> Con Choyero! Marketplace Logístico
              </p>
              <div className="space-y-2 text-sm text-gray-300">
                <p>✅ Cotizaciones en menos de 15 minutos</p>
                <p>✅ Comparas precios y eliges libremente</p>
                <p>✅ Tu dinero en garantía hasta la entrega</p>
                <p>✅ GPS en tiempo real desde tu celular</p>
                <p>✅ CFDI automático por cada servicio</p>
                <p>✅ Menos CO₂ al aprovechar retornos vacíos</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="bg-orange-500 py-10">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <div key={i} className="text-center">
              <p className="text-4xl font-extrabold text-white">{s.valor}</p>
              <p className="text-orange-100 text-sm mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CÓMO FUNCIONA ── */}
      <section id="como-funciona" className="py-24 bg-gray-950">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">Así de simple</h2>
            <p className="text-gray-400 text-lg">Elige tu perfil y ve cómo Choyero! trabaja para ti</p>
          </div>

          {/* Tabs */}
          <div className="flex justify-center mb-12">
            <div className="bg-gray-800 rounded-xl p-1 flex gap-1">
              <button
                onClick={() => setTab('cliente')}
                className={`px-6 py-3 rounded-lg font-semibold text-sm transition ${tab === 'cliente' ? 'bg-gray-900 text-orange-400 shadow' : 'text-gray-400 hover:text-gray-200'}`}
              >
                <Package size={16} className="inline mr-2" />
                Soy cliente
              </button>
              <button
                onClick={() => setTab('transportista')}
                className={`px-6 py-3 rounded-lg font-semibold text-sm transition ${tab === 'transportista' ? 'bg-gray-900 text-orange-400 shadow' : 'text-gray-400 hover:text-gray-200'}`}
              >
                <Truck size={16} className="inline mr-2" />
                Soy transportista
              </button>
            </div>
          </div>

          {/* Pasos */}
          <div className="grid md:grid-cols-3 gap-8">
            {(tab === 'cliente' ? pasosCliente : pasosTransportista).map((p, i) => (
              <div key={i} className="text-center group">
                <div className="relative inline-flex">
                  <div className="w-16 h-16 bg-orange-100 group-hover:bg-orange-500 rounded-2xl flex items-center justify-center mb-4 transition mx-auto text-orange-500 group-hover:text-white">
                    {p.icono}
                  </div>
                  <span className="absolute -top-2 -right-2 w-6 h-6 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {i + 1}
                  </span>
                </div>
                <h3 className="text-white font-bold text-lg mb-2">{p.titulo}</h3>
                <p className="text-gray-200 text-sm leading-relaxed">{p.desc}</p>
                {i < 2 && (
                  <ChevronRight size={20} className="hidden md:block absolute right-0 top-1/2 text-gray-300" />
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button
              onClick={tab === 'cliente' ? onRegistrarCliente : onRegistrarTransportista}
              className="group inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-8 py-4 rounded-xl transition shadow-lg shadow-orange-500/20"
            >
              {tab === 'cliente' ? 'Registrarme como cliente' : 'Registrar mi empresa'}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* ── BENEFICIOS ── */}
      <section id="beneficios" className="py-24 bg-gray-950">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">Todo lo que necesitas, en un solo lugar</h2>
            <p className="text-gray-400 text-lg">Diseñado para ambos lados del negocio logístico</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Cliente */}
            <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Package size={20} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg">Para clientes</h3>
                  <p className="text-gray-200 text-sm">Empresas que envían mercancía</p>
                </div>
              </div>
              <ul className="space-y-3">
                {beneficiosCliente.map((b, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <span className="text-blue-500 flex-shrink-0">{b.icono}</span>
                    <span className="text-white text-sm">{b.texto}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={onRegistrarCliente}
                className="mt-8 w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3 rounded-xl transition"
              >
                Registrarme como cliente
              </button>
            </div>

            {/* Transportista */}
            <div id="transportistas" className="bg-gray-900 rounded-2xl p-8 shadow-sm border border-gray-800">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-orange-500/20 rounded-xl flex items-center justify-center">
                  <Truck size={20} className="text-orange-400" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg">Para transportistas</h3>
                  <p className="text-gray-200 text-sm">Empresas de transporte B2B</p>
                </div>
              </div>
              <ul className="space-y-3">
                {beneficiosTransportista.map((b, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <span className="text-orange-400 flex-shrink-0">{b.icono}</span>
                    <span className="text-white text-sm">{b.texto}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={onRegistrarTransportista}
                className="mt-8 w-full bg-orange-500 hover:bg-orange-400 text-white font-semibold py-3 rounded-xl transition"
              >
                Registrar mi empresa
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIAL / CONFIANZA ── */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icono: <Shield size={28} />, titulo: 'Transportistas verificados', desc: 'Revisamos licencias, seguros y antecedentes antes de aprobar cualquier transportista en la plataforma.' },
              { icono: <MapPin size={28} />, titulo: 'Rastreo GPS en tiempo real', desc: 'Desde que el chofer sale hasta que entrega, puedes ver la ubicación exacta de tu carga.' },
              { icono: <Star size={28} />,   titulo: 'Sistema de calificaciones', desc: 'Cada viaje genera una calificación. Los mejores transportistas ganan más visibilidad.' },
            ].map((f, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4 text-orange-500">
                  {f.icono}
                </div>
                <h3 className="font-bold text-white mb-2">{f.titulo}</h3>
                <p className="text-gray-200 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── RETORNOS VACÍOS ── */}
      <section className="py-24 bg-gray-950 border-t border-gray-900">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-4 py-1.5 mb-6">
                <RefreshCw size={14} className="text-orange-400" />
                <span className="text-orange-400 text-sm font-medium">Solo en Choyero!</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
                Tu camión regresa vacío.<br />
                <span className="text-orange-400">Eso ya no tiene que ser así.</span>
              </h2>
              <p className="text-gray-300 text-lg mb-6">
                Cada viaje de regreso vacío es dinero perdido. Con los <strong className="text-white">Fletes de Retorno</strong> de Choyero!, publicas el espacio disponible en tu camión y clientes de la ruta pagan por ese espacio.
              </p>
              <div className="space-y-3 mb-8">
                <div className="flex items-start gap-3">
                  <CheckCircle size={18} className="text-green-400 shrink-0 mt-0.5" />
                  <p className="text-gray-300 text-sm">Publicas origen, destino y capacidad disponible — tarda 1 minuto</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle size={18} className="text-green-400 shrink-0 mt-0.5" />
                  <p className="text-gray-300 text-sm">Clientes de la ruta ven tu oferta y hacen propuestas de precio</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle size={18} className="text-green-400 shrink-0 mt-0.5" />
                  <p className="text-gray-300 text-sm">Aceptas, cobras seguro vía escrow y listo — el viaje que ibas a hacer de todas formas ahora genera ingreso extra</p>
                </div>
                <div className="flex items-start gap-3">
                  <Leaf size={18} className="text-green-400 shrink-0 mt-0.5" />
                  <p className="text-gray-300 text-sm">Cada retorno aprovechado evita un viaje vacío — hasta <strong className="text-green-400">1.7 toneladas de CO₂</strong> menos por trayecto Tijuana–La Paz</p>
                </div>
              </div>
              <button onClick={onRegistrarTransportista} className="group inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-6 py-3 rounded-xl transition shadow-lg shadow-orange-500/20">
                Registrar mi empresa
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 space-y-4">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest">Ejemplo real</p>
              <div className="bg-gray-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-white font-bold text-sm">Tijuana → La Paz</p>
                    <p className="text-gray-400 text-xs mt-0.5">Regreso con espacio disponible · 8 ton</p>
                  </div>
                  <span className="bg-orange-500/20 text-orange-400 text-xs font-bold px-2 py-1 rounded-lg">Retorno</span>
                </div>
                <div className="border-t border-gray-700 pt-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Sin Choyero!</span>
                    <span className="text-red-400 font-semibold">$0 ingreso · 1.7 ton CO₂</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Con retorno Choyero!</span>
                    <span className="text-green-400 font-bold">+ ingreso · −CO₂</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-500 text-xs text-center">Disponible solo para transportistas registrados en Choyero!</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── IMPACTO AMBIENTAL ── */}
      <section className="py-20 bg-gray-900 border-t border-gray-800">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-full px-4 py-1.5 mb-4">
              <Leaf size={14} className="text-green-400" />
              <span className="text-green-400 text-sm font-medium">Logística Verde</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
              Menos camiones vacíos.<br />
              <span className="text-green-400">Menos CO₂ en la carretera.</span>
            </h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              La logística tradicional mueve toneladas de aire. Cada camión que regresa sin carga quema combustible y emite CO₂ sin producir valor. Choyero convierte esos viajes vacíos en rutas productivas.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              {
                icon: <RefreshCw size={28} className="text-green-400" />,
                titulo: 'Retornos Vacíos',
                desc: 'Aprovechamos el espacio en camiones que ya están en ruta. Un viaje de regreso lleno emite la misma cantidad de CO₂ que uno vacío, pero transporta valor real.',
                stat: '−50%', statLabel: 'emisiones por ton transportada'
              },
              {
                icon: <Globe size={28} className="text-green-400" />,
                titulo: 'Rutas Optimizadas',
                desc: 'Conectamos carga con el transportista más cercano al origen, reduciendo kilómetros de traslado en vacío antes de iniciar el flete.',
                stat: 'Local', statLabel: 'transportistas notificados primero'
              },
              {
                icon: <Leaf size={28} className="text-green-400" />,
                titulo: 'Plataforma Digital',
                desc: 'Eliminamos llamadas, papeles y visitas innecesarias. Todo el proceso — cotización, pago, factura y seguimiento — ocurre en línea.',
                stat: '0',  statLabel: 'papel en todo el proceso'
              },
            ].map((item, i) => (
              <div key={i} className="bg-gray-800 border border-gray-700 rounded-2xl p-6">
                <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center mb-4">
                  {item.icon}
                </div>
                <h3 className="font-bold text-white mb-2">{item.titulo}</h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">{item.desc}</p>
                <div className="border-t border-gray-700 pt-4">
                  <p className="text-2xl font-extrabold text-green-400">{item.stat}</p>
                  <p className="text-gray-500 text-xs mt-0.5">{item.statLabel}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-green-950/30 border border-green-800/40 rounded-2xl p-6 text-center">
            <p className="text-green-300 text-sm font-semibold mb-1">¿Sabías esto?</p>
            <p className="text-gray-300">Un camión de carga vacío en la ruta Tijuana–La Paz emite aproximadamente <strong className="text-green-400">1.7 toneladas de CO₂</strong>. Cada retorno aprovechado a través de Choyero evita esa emisión — equivalente a la absorción anual de CO₂ de <strong className="text-green-400">77 árboles</strong>.</p>
          </div>
        </div>
      </section>

      {/* ── GARANTÍA ESCROW ── */}
      <section className="py-20 bg-gray-900 border-y border-gray-800">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Lock size={36} className="text-orange-400 mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-4">
            Tu dinero no se mueve hasta que tú lo digas
          </h2>
          <p className="text-gray-300 text-lg mb-10 max-w-2xl mx-auto">
            Al contratar un flete, tu pago queda retenido en garantía (escrow) a través de MercadoPago. El transportista recibe el dinero <strong className="text-white">únicamente cuando confirmas que tu carga llegó correctamente</strong>. Si no confirmas en 72 horas, se libera automáticamente.
          </p>
          <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {[
              { paso: '1', titulo: 'Pagas al contratar', desc: 'El monto queda retenido en garantía. No llega al transportista.', color: 'bg-blue-500' },
              { paso: '2', titulo: 'Tu carga viaja', desc: 'Rastreo GPS en tiempo real. El dinero sigue en garantía.', color: 'bg-orange-500' },
              { paso: '3', titulo: 'Confirmas la entrega', desc: 'Solo entonces el pago se libera al transportista.', color: 'bg-green-500' },
            ].map((p, i) => (
              <div key={i} className="bg-gray-800 rounded-xl p-5 text-left">
                <div className={`w-8 h-8 ${p.color} rounded-full flex items-center justify-center text-white font-bold text-sm mb-3`}>{p.paso}</div>
                <p className="text-white font-semibold text-sm mb-1">{p.titulo}</p>
                <p className="text-gray-400 text-xs">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROGRAMA DE REFERIDOS ── */}
      <section id="referidos" className="py-24 bg-gray-950">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-4 py-1.5 mb-5">
              <TrendingUp size={14} className="text-orange-400" />
              <span className="text-orange-400 text-sm font-medium">Red de referidos multinivel</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
              Gana dinero extra recomendando Choyero!
            </h2>
            <p className="text-gray-200 text-lg max-w-2xl mx-auto">
              Comparte Choyero! con transportistas o empresas de tu región y recibe comisiones cada vez que hagan un flete. Ellos trabajan, tú cobras. Automático, mes a mes. Cobras directo a tu banco vía SPEI.
            </p>
            <p className="text-gray-400 text-sm mt-3">
              Sin inversión ni experiencia. Solo tu celular y tus contactos.
            </p>
          </div>

          {/* Niveles */}
          <div className="grid md:grid-cols-3 gap-6 mb-14">
            {redCfgCargando ? (
              // Skeleton mientras carga la configuración real
              [1, 2, 3].map(i => (
                <div key={i} className="bg-gray-900 border border-gray-800 rounded-2xl p-6 animate-pulse">
                  <div className="w-14 h-14 bg-gray-700 rounded-2xl mx-auto mb-4" />
                  <div className="h-4 bg-gray-700 rounded w-24 mx-auto mb-3" />
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-700 rounded w-full" />
                    <div className="h-3 bg-gray-700 rounded w-4/5" />
                    <div className="h-3 bg-gray-700 rounded w-full" />
                    <div className="h-3 bg-gray-700 rounded w-3/5" />
                  </div>
                </div>
              ))
            ) : (
              [
                { nivel: '1er nivel', pctTrans: redCfg?.pctFleteN1, pctCliente: redCfg?.pctFleteClienteN1, bonoTrans: redCfg?.bonoTransN1, bonoCliente: redCfg?.bonoClienteN1, color: 'from-orange-500 to-orange-600' },
                { nivel: '2do nivel', pctTrans: redCfg?.pctFleteN2, pctCliente: redCfg?.pctFleteClienteN2, bonoTrans: redCfg?.bonoTransN2, bonoCliente: redCfg?.bonoClienteN2, color: 'from-orange-600 to-orange-800' },
                { nivel: '3er nivel', pctTrans: redCfg?.pctFleteN3, pctCliente: redCfg?.pctFleteClienteN3, bonoTrans: redCfg?.bonoTransN3, bonoCliente: redCfg?.bonoClienteN3, color: 'from-orange-700 to-orange-900' },
              ].map((n, i) => (
                <div key={i} className="bg-gray-900 border border-gray-800 rounded-2xl p-6 text-center">
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${n.color} mb-4`}>
                    <span className="text-white font-extrabold text-lg">{n.pctTrans != null ? `${n.pctTrans}%` : '?'}</span>
                  </div>
                  <h3 className="text-white font-bold text-lg mb-3">{n.nivel}</h3>
                  <div className="space-y-2 text-sm text-left">
                    <div className="flex items-start gap-2">
                      <span className="text-orange-400 font-bold shrink-0">🚛</span>
                      <p className="text-gray-300">
                        <span className="font-semibold text-white">{n.pctTrans != null ? `${n.pctTrans}%` : '?%'}</span> por cada flete de transportistas que refieras
                        {n.bonoTrans > 0 && <span className="text-green-400"> + bono de <span className="font-semibold">${n.bonoTrans.toLocaleString('es-MX')}</span> al primer flete</span>}
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-blue-400 font-bold shrink-0">📦</span>
                      <p className="text-gray-300">
                        <span className="font-semibold text-white">{n.pctCliente != null ? `${n.pctCliente}%` : '?%'}</span> por cada flete de empresas cliente que refieras
                        {n.bonoCliente > 0 && <span className="text-green-400"> + bono de <span className="font-semibold">${n.bonoCliente.toLocaleString('es-MX')}</span> al primer flete</span>}
                      </p>
                    </div>
                    <p className="text-gray-500 text-xs pt-1">% sobre la comisión de la plataforma</p>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Cómo funciona */}
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
            <h3 className="text-white font-bold text-lg mb-6 text-center">¿Cómo funciona?</h3>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { num: '1', titulo: 'Regístrate gratis', desc: 'Crea tu cuenta como persona física y recibe tu enlace único de referido.' },
                { num: '2', titulo: 'Comparte tu enlace', desc: 'Recomienda Choyero! a transportistas o empresas que necesiten fletes usando tu enlace.' },
                { num: '3', titulo: 'Cobra comisiones', desc: 'Cada viaje que completen tus referidos genera comisiones automáticas en tu cuenta.' },
              ].map((p, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white text-sm">{p.num}</div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">{p.titulo}</h4>
                    <p className="text-gray-200 text-sm">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-10">
            <a
              href="/referidos"
              className="group inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-8 py-4 rounded-xl transition shadow-lg shadow-orange-500/20"
            >
              Registrarme y empezar a referir
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <p className="text-gray-500 text-xs mt-3">Solo para personas físicas · Sin costo · Sin compromiso</p>
          </div>
        </div>
      </section>

      {/* ── CTA FINAL ── */}
      <section className="py-24 bg-gray-950 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-orange-500 rounded-full opacity-10 blur-3xl" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6">
            ¿Listo para mover tu negocio?
          </h2>
          <p className="text-gray-400 text-lg mb-10">
            La plataforma logística de la Península de Baja California.<br />
            Únete a las empresas que ya mueven su carga con Choyero!.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={onRegistrarCliente}
              className="group flex flex-col items-center justify-center gap-1 bg-orange-500 hover:bg-orange-400 text-white font-bold px-8 py-4 rounded-xl text-lg transition shadow-lg shadow-orange-500/25"
            >
              <span className="flex items-center gap-2">
                <Package size={20} />
                Soy cliente
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </span>
              <span className="text-xs font-normal opacity-80">Cotiza tu flete</span>
            </button>
            <button
              onClick={onRegistrarTransportista}
              className="flex flex-col items-center justify-center gap-1 border border-gray-700 hover:border-orange-500 text-white font-semibold px-8 py-4 rounded-xl text-lg transition"
            >
              <span className="flex items-center gap-2">
                <Truck size={20} className="text-orange-400" />
                Soy transportista
              </span>
              <span className="text-xs font-normal text-gray-400">Tengo una empresa de transporte</span>
            </button>
          </div>
          <p className="text-gray-400 text-sm mt-6">
            ¿Ya tienes cuenta?{' '}
            <button onClick={onIniciarSesion} className="text-orange-400 hover:text-orange-300 font-medium transition">
              Inicia sesión
            </button>
          </p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-gray-950 border-t border-gray-800 pt-12 pb-6">
        <div className="max-w-6xl mx-auto px-6">
          {/* Fila principal */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">
            {/* Logo + descripción */}
            <div>
              <img src="/logo-choyero.png" alt="Choyero" className="h-10 w-auto mb-3" />
              <p className="text-gray-400 text-sm leading-relaxed">
                El primer marketplace logístico B2B exclusivo para la Península de Baja California.
              </p>
            </div>

            {/* Contacto */}
            <div>
              <p className="text-white font-semibold text-sm mb-4 uppercase tracking-wider">Contacto</p>
              <div className="flex flex-col gap-3">
                <a href="mailto:contact@choyero.app" className="flex items-center gap-2 text-gray-400 hover:text-orange-400 text-sm transition">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  contact@choyero.app
                </a>
                <a href="https://wa.me/526151558086" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-400 hover:text-green-400 text-sm transition">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  +52 615 155 8086
                </a>
              </div>
            </div>

            {/* Links + Redes */}
            <div>
              <p className="text-white font-semibold text-sm mb-4 uppercase tracking-wider">Plataforma</p>
              <div className="flex flex-col gap-2 mb-6">
                <button onClick={onIniciarSesion} className="text-gray-400 hover:text-orange-400 text-sm transition text-left">Iniciar sesión</button>
                <button onClick={onRegistrarCliente} className="text-gray-400 hover:text-orange-400 text-sm transition text-left">Soy cliente</button>
                <button onClick={onRegistrarTransportista} className="text-gray-400 hover:text-orange-400 text-sm transition text-left">Soy transportista</button>
              </div>
              {/* Redes sociales */}
              <div className="flex gap-4">
                <a href="https://www.facebook.com/profile.php?id=61589490730300" target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-gray-800 hover:bg-blue-600 flex items-center justify-center transition" title="Facebook">
                  <svg className="w-4 h-4 text-gray-400 hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="https://www.linkedin.com/in/vicente-cepeda-lozano-b278a4407/" target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-gray-800 hover:bg-blue-700 flex items-center justify-center transition" title="LinkedIn">
                  <svg className="w-4 h-4 text-gray-400 hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-gray-800 pt-6 text-center">
            <p className="text-gray-500 text-xs">© {new Date().getFullYear()} Choyero! Todos los derechos reservados. · Vizcaíno, Baja California Sur, México</p>
          </div>
        </div>
      </footer>

    </div>
  );
}

function NavCompleto({ onIniciarSesion, onRegistrar }) {
  const [navAbierto, setNavAbierto] = useState(false);
  return (
    <nav className="bg-gray-950 border-b border-gray-900 px-6 py-4">
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        <a href="/"><img src="/logo-choyero.png" alt="Choyero" className="h-10 w-auto" /></a>
        {/* Desktop */}
        <div className="hidden md:flex items-center gap-6">
          <a href="/#como-funciona" className="text-gray-300 hover:text-white text-sm transition">Cómo funciona</a>
          <a href="/#beneficios" className="text-gray-300 hover:text-white text-sm transition">Beneficios</a>
          <a href="/referidos" className="text-gray-300 hover:text-white text-sm transition">Programa de referidos</a>
          <a href="/quienessomos" className="text-gray-300 hover:text-white text-sm transition">Quiénes somos</a>
          {onIniciarSesion && <button onClick={onIniciarSesion} className="text-gray-300 hover:text-white text-sm transition">Iniciar sesión</button>}
          {onRegistrar && <button onClick={onRegistrar} className="border border-gray-600 hover:border-orange-500 text-white font-semibold px-4 py-2 rounded-lg text-sm transition">Registrarme</button>}
        </div>
        {/* Hamburger móvil */}
        <button className="md:hidden text-white" onClick={() => setNavAbierto(!navAbierto)}>
          {navAbierto ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {/* Menú móvil */}
      {navAbierto && (
        <div className="md:hidden bg-gray-950 border-t border-gray-800 px-6 py-4 space-y-3">
          <a href="/#como-funciona" onClick={() => setNavAbierto(false)} className="block text-gray-300 hover:text-white text-sm">Cómo funciona</a>
          <a href="/#beneficios" onClick={() => setNavAbierto(false)} className="block text-gray-300 hover:text-white text-sm">Beneficios</a>
          <a href="/referidos" onClick={() => setNavAbierto(false)} className="block text-gray-300 hover:text-white text-sm">Programa de referidos</a>
          <a href="/quienessomos" onClick={() => setNavAbierto(false)} className="block text-gray-300 hover:text-white text-sm">Quiénes somos</a>
          {onIniciarSesion && <button onClick={onIniciarSesion} className="w-full border border-gray-700 text-white py-2.5 rounded-lg text-sm">Iniciar sesión</button>}
          {onRegistrar && <button onClick={onRegistrar} className="w-full bg-orange-500 text-white font-semibold py-2.5 rounded-lg text-sm">Registrarme</button>}
        </div>
      )}
    </nav>
  );
}

export function PaginaReferidos({ onRegistrar, onIniciarSesion }) {
  const [redCfg, setRedCfg] = useState(null);

  useEffect(() => {
    fetch(`${BASE_URL}/admin/config`)
      .then(r => r.json())
      .then(d => { if (d?.config?.redMercadeo) setRedCfg(d.config.redMercadeo); })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-gray-950 font-sans flex flex-col">
      <NavCompleto onIniciarSesion={onIniciarSesion} onRegistrar={onRegistrar} />

      {/* HERO */}
      <div className="flex-1 max-w-4xl mx-auto w-full px-6 py-16 text-center">
        <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-4 py-1.5 mb-6">
          <Users size={14} className="text-orange-400" />
          <span className="text-orange-400 text-sm font-medium">Programa de Referidos — Solo para miembros invitados</span>
        </div>
        <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-4">
          Gana comisiones<br /><span className="text-orange-400">refiriendo clientes.</span>
        </h1>
        <p className="text-gray-300 text-lg mb-10 max-w-xl mx-auto">
          Conecta transportistas y clientes con Choyero y recibe un porcentaje de cada flete que se genere a través de tu red.
        </p>
        <button
          onClick={onRegistrar}
          className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-8 py-4 rounded-xl text-lg transition shadow-lg shadow-orange-500/25"
        >
          Registrarme como Referidor
          <ArrowRight size={18} />
        </button>
        <p className="text-gray-500 text-sm mt-4">
          ¿Ya tienes cuenta?{' '}
          <button onClick={onIniciarSesion} className="text-orange-400 hover:text-orange-300 transition">
            Inicia sesión aquí
          </button>
        </p>

        {/* NIVELES DE COMISIÓN */}
        <div className="grid md:grid-cols-3 gap-4 mt-14 text-left">
          {[
            { n: 1, color: 'orange', pct: redCfg?.pctFleteN1 ?? 10, bono: redCfg?.bonoTransN1 },
            { n: 2, color: 'purple', pct: redCfg?.pctFleteN2 ?? 5,  bono: redCfg?.bonoTransN2 },
            { n: 3, color: 'blue',   pct: redCfg?.pctFleteN3 ?? 3,  bono: redCfg?.bonoTransN3 },
          ].map(({ n, color, pct, bono }) => (
            <div key={n} className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl font-extrabold mb-3 ${color === 'orange' ? 'bg-orange-500 text-white' : color === 'purple' ? 'bg-purple-600 text-white' : 'bg-blue-600 text-white'}`}>
                {pct}%
              </div>
              <h3 className="text-white font-bold text-base mb-1">{n}er nivel</h3>
              <p className="text-gray-400 text-sm">
                {pct}% de la comisión Choyero por cada flete de tus referidos
                {bono ? <span className="block text-green-400 font-medium mt-0.5">+ bono ${bono.toLocaleString()} al primer flete</span> : null}
              </p>
            </div>
          ))}
        </div>

        {/* BENEFICIOS EXTRA */}
        <div className="grid md:grid-cols-2 gap-4 mt-4 text-left">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex gap-4">
            <TrendingUp size={24} className="text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-white font-bold text-sm mb-0.5">Red que trabaja sola</h3>
              <p className="text-gray-400 text-xs">Cada flete que completen tus referidos genera comisiones automáticas en tu cuenta.</p>
            </div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex gap-4">
            <Shield size={24} className="text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-white font-bold text-sm mb-0.5">Retiros seguros vía SPEI</h3>
              <p className="text-gray-400 text-xs">Solicita tu retiro cuando quieras. Transferencia directa a tu CLABE interbancaria.</p>
            </div>
          </div>
        </div>

        {/* CTA FINAL */}
        <div className="mt-12 bg-gradient-to-r from-orange-500/10 to-orange-600/5 border border-orange-500/20 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-white mb-2">¿Listo para empezar?</h2>
          <p className="text-gray-400 mb-6">Regístrate y obtén tu código de referido único. Sin costo, sin compromisos.</p>
          <button
            onClick={onRegistrar}
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-8 py-3 rounded-xl transition"
          >
            Crear cuenta Referidor <ArrowRight size={16} />
          </button>
        </div>
      </div>

      {/* FOOTER */}
      <footer className="border-t border-gray-900 py-6 text-center">
        <p className="text-gray-600 text-sm">© {new Date().getFullYear()} Choyero! Todos los derechos reservados.</p>
      </footer>
    </div>
  );
}

export function PaginaQuienessomos({ onIniciarSesion, onRegistrar }) {
  const valores = [
    {
      icon: <Shield size={28} className="text-orange-400" />,
      titulo: 'Transparencia',
      desc: 'Precios claros, comisiones visibles y trazabilidad completa de cada flete. Sin letra pequeña ni costos ocultos.',
    },
    {
      icon: <CheckCircle size={28} className="text-orange-400" />,
      titulo: 'Confiabilidad',
      desc: 'Transportistas verificados, calificaciones reales y sistema de escrow que protege el pago de ambas partes.',
    },
    {
      icon: <Globe size={28} className="text-orange-400" />,
      titulo: 'Economía local',
      desc: 'Priorizamos el talento y las empresas de Baja California y BCS, fortaleciendo la cadena logística regional.',
    },
    {
      icon: <Leaf size={28} className="text-orange-400" />,
      titulo: 'Sostenibilidad',
      desc: 'Optimizamos rutas y reducimos viajes en vacío para disminuir emisiones y aprovechar mejor cada traslado.',
    },
  ];

  const diferenciadores = [
    {
      titulo: 'Cotización en minutos',
      desc: 'El cliente publica su solicitud y recibe propuestas de transportistas verificados en la región sin necesidad de llamadas ni intermediarios.',
    },
    {
      titulo: 'Pago seguro con escrow',
      desc: 'El monto queda retenido hasta confirmar la entrega. El transportista cobra cuando cumple y el cliente tiene respaldo en todo momento.',
    },
    {
      titulo: 'Documentación digital integrada',
      desc: 'Carta porte, firma de conformidad y factura CFDI generados dentro de la misma plataforma, sin papeleo adicional.',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-950 font-sans flex flex-col">
      {/* NAV */}
      <NavCompleto onIniciarSesion={onIniciarSesion} onRegistrar={onRegistrar} />

      <div className="flex-1 max-w-5xl mx-auto w-full px-6 py-16">
        {/* ENCABEZADO */}
        <div className="text-center mb-16">
          <p className="text-orange-400 text-sm font-semibold uppercase tracking-widest mb-3">Quiénes somos</p>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-6">
            El marketplace logístico<br />
            <span className="text-orange-400">de la Península de Baja California</span>
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto leading-relaxed">
            Choyero es una plataforma digital que conecta a empresas y particulares con transportistas verificados de la región,
            facilitando la contratación de fletes de manera transparente, segura y completamente en línea.
          </p>
        </div>

        {/* MISIÓN */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                <Truck size={20} className="text-orange-400" />
              </div>
              <h2 className="text-white font-bold text-xl">Misión</h2>
            </div>
            <p className="text-gray-300 leading-relaxed">
              Conectar a empresas y particulares de Baja California y Baja California Sur con transportistas verificados
              de la región, ofreciendo una plataforma digital que hace la contratación de fletes más ágil, transparente
              y segura, impulsando la economía local y la eficiencia logística en la Península.
            </p>
          </div>

          {/* VISIÓN */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                <TrendingUp size={20} className="text-orange-400" />
              </div>
              <h2 className="text-white font-bold text-xl">Visión</h2>
            </div>
            <p className="text-gray-300 leading-relaxed">
              Ser la plataforma de referencia en logística regional para la Península de Baja California, reconocida por
              transformar la manera en que empresas y transportistas se conectan, generando confianza, eficiencia y
              oportunidades de crecimiento para todos los actores de la cadena logística.
            </p>
          </div>
        </div>

        {/* VALORES */}
        <div className="mb-16">
          <h2 className="text-white font-bold text-2xl text-center mb-8">Nuestros valores</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {valores.map((v) => (
              <div key={v.titulo} className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col gap-3">
                <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center">
                  {v.icon}
                </div>
                <h3 className="text-white font-bold text-base">{v.titulo}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* POR QUÉ CHOYERO */}
        <div className="mb-16">
          <h2 className="text-white font-bold text-2xl text-center mb-2">¿Por qué Choyero?</h2>
          <p className="text-gray-400 text-center mb-8">
            Frente al proceso tradicional de contratar un flete por recomendación, llamadas y efectivo, Choyero ofrece:
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {diferenciadores.map((d, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <div className="w-8 h-8 rounded-lg bg-orange-500 text-white font-extrabold flex items-center justify-center text-sm mb-4">
                  {i + 1}
                </div>
                <h3 className="text-white font-bold text-base mb-2">{d.titulo}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA — solo visible si el usuario no está autenticado */}
        {(onRegistrar || onIniciarSesion) && (
          <div className="bg-gradient-to-r from-orange-500/10 to-orange-600/5 border border-orange-500/20 rounded-2xl p-10 text-center">
            <h2 className="text-2xl font-bold text-white mb-3">Únete a Choyero</h2>
            <p className="text-gray-400 mb-8 max-w-lg mx-auto">
              Ya sea que necesites mover carga o quieras ofrecer tus servicios de transporte, tenemos un espacio para ti.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {onRegistrar && (
                <button
                  onClick={onRegistrar}
                  className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-8 py-3 rounded-xl transition shadow-lg shadow-orange-500/25"
                >
                  Crear cuenta <ArrowRight size={16} />
                </button>
              )}
              {onIniciarSesion && (
                <button
                  onClick={onIniciarSesion}
                  className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-8 py-3 rounded-xl transition"
                >
                  Iniciar sesión
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* FOOTER */}
      <footer className="border-t border-gray-900 py-6 text-center mt-8">
        <p className="text-gray-600 text-sm">© {new Date().getFullYear()} Choyero! Todos los derechos reservados.</p>
      </footer>
    </div>
  );
}
